import { Package, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

interface Product {
  id: string;
  name: string;
  base_price: number;
  is_active: boolean;
  category: string | null;
  tier: string | null;
}

interface CategoryOverviewProps {
  products: Product[] | undefined;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function CategoryOverview({ products }: CategoryOverviewProps) {
  if (!products || products.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Categories Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No products yet</p>
            <p className="text-sm">Add products to see category breakdown</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const categoryStats = products.reduce((acc, product) => {
    const category = product.category || "Uncategorized";
    if (!acc[category]) {
      acc[category] = {
        count: 0,
        activeCount: 0,
        totalValue: 0,
        products: [],
      };
    }
    acc[category].count++;
    if (product.is_active) acc[category].activeCount++;
    acc[category].totalValue += product.base_price;
    acc[category].products.push(product);
    return acc;
  }, {} as Record<string, { count: number; activeCount: number; totalValue: number; products: Product[] }>);

  const totalProducts = products.length;
  const categories = Object.entries(categoryStats).sort((a, b) => b[1].count - a[1].count);

  const categoryColors: Record<string, string> = {
    Software: "bg-blue-500",
    Services: "bg-green-500",
    Hardware: "bg-purple-500",
    Support: "bg-orange-500",
    Subscription: "bg-cyan-500",
    Consulting: "bg-pink-500",
    Training: "bg-amber-500",
    Uncategorized: "bg-gray-500",
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Categories Overview
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {categories.map(([category, stats]) => {
            const percentage = (stats.count / totalProducts) * 100;
            const colorClass = categoryColors[category] || "bg-primary";

            return (
              <div key={category} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${colorClass}`} />
                    <span className="font-medium capitalize">{category}</span>
                    <Badge variant="secondary" className="text-xs">
                      {stats.count} products
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-muted-foreground">
                      {stats.activeCount} active
                    </span>
                    <span className="font-medium">
                      {formatCurrency(stats.totalValue)}
                    </span>
                  </div>
                </div>
                <Progress value={percentage} className="h-2" />
                <div className="flex flex-wrap gap-1">
                  {stats.products.slice(0, 3).map((product) => (
                    <Badge key={product.id} variant="outline" className="text-xs">
                      {product.name}
                    </Badge>
                  ))}
                  {stats.products.length > 3 && (
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      +{stats.products.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
